import { Box } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { ErrorText } from 'components/domain/ErrorText'
import dayjs, { type Dayjs } from 'dayjs'
import { Controller, type Control, type FieldErrors } from 'react-hook-form'
import { FormLabel } from '../Label/FormLabel'

interface DatePickerProps {
  name: string
  control: Control<any>
  label: string
  defaultValue?: string | null
  errors: FieldErrors<any>
  disablePast?: boolean
  disableWeekends?: boolean
}

const isWeekend = (date: Dayjs) => {
  const day = date.day()

  return day === 0 || day === 6
}

const FormDatePicker: React.FC<DatePickerProps> = ({ name, control, label, defaultValue, errors, disablePast = false, disableWeekends = false }: DatePickerProps) => {
  const inputId = `date-picker-${name}`
  const errorId = `error-${name}`
  const hasError = errors[name] != null

  const shouldDisableDate = (date: Dayjs) => {
    if (disableWeekends && isWeekend(date)) return true
    return false
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Controller
        name={name}
        control={control}
        defaultValue={defaultValue ?? dayjs()}
        render={({ field }) => (
          <Box mb={2}>
            <FormLabel htmlFor={inputId}>
              {label.toUpperCase()}
            </FormLabel>
            <DatePicker
              sx={{ width: '100%' }}
              shouldDisableDate={shouldDisableDate}
              disablePast={disablePast}
              value={dayjs(field.value)}
              onChange={(date) => { field.onChange(date) }}
              slotProps={{
                textField: {
                  id: inputId,
                  error: hasError,
                  inputProps: {
                    'aria-invalid': hasError,
                    'aria-describedby': hasError ? errorId : undefined
                  }
                }
              }}
            />
            {hasError && (
              <ErrorText id={errorId} role="alert">
                {String(errors[name]?.message)}
              </ErrorText>
            )}
          </Box>
        )}
      />
    </LocalizationProvider>
  )
}

export default FormDatePicker
